// src/hooks/useGaslessPurchase.js
// Gasless ticket purchase via server relayer (server pays gas).
// Signs EIP-712 MintTicketBatch, then POSTs to relayer endpoint.
// Falls back to direct on-chain call if relayer is unavailable.
import { useState, useCallback } from 'react';
import { BrowserProvider, Contract, keccak256, AbiCoder, solidityPacked } from 'ethers';
import { getDomain } from '../utils/eip712';
import EventTicketABI from '../abis/EventTicket.json';

const _BigInt = typeof globalThis.BigInt !== 'undefined' ? globalThis.BigInt : (v) => Number(v);
const _abiCoder = new AbiCoder();

// Relayer URL: prefer PHP relayer (?action=), fall back to Express REST-style
const RELAYER_PHP_URL = (process.env.REACT_APP_RELAYER_URL || '').trim();
const RELAYER_BASE_URL = (process.env.REACT_APP_RELAYER_BASE_URL || '').trim();
const API_URL = (process.env.REACT_APP_API_URL || '').trim();
const RELAYER_API_KEY = (process.env.REACT_APP_RELAYER_API_KEY || '').trim();

function getRelayerMintUrl() {
  // PHP relayer: relayer.php?action=mint-ticket-batch
  if (RELAYER_PHP_URL) {
    const sep = RELAYER_PHP_URL.includes('?') ? '&' : '?';
    return `${RELAYER_PHP_URL}${sep}action=mint-ticket-batch`;
  }
  // Express server: /api/relayer/mint-ticket-batch
  if (RELAYER_BASE_URL) return `${RELAYER_BASE_URL}/mint-ticket-batch`;
  if (API_URL) return `${API_URL}/api/relayer/mint-ticket-batch`;
  return null;
}

// EIP-712 types must match contract
const TYPES_BATCH = {
  MintTicketBatch: [
    { name: 'user', type: 'address' },
    { name: 'itemsHash', type: 'bytes32' },
    { name: 'nonce', type: 'uint256' },
  ],
};

const MINT_ITEM_TYPEHASH = keccak256(new TextEncoder().encode('MintItem(uint256 ticketTypeId,uint256 amount)'));

function computeItemsHash(items) {
  const sorted = [...items].sort((a, b) => a.ticketTypeId - b.ticketTypeId);
  const hashes = sorted.map(item => {
    const encoded = _abiCoder.encode(
      ['bytes32', 'uint256', 'uint256'],
      [MINT_ITEM_TYPEHASH, _BigInt(item.ticketTypeId), _BigInt(item.amount)]
    );
    return keccak256(encoded);
  });
  const packed = solidityPacked(hashes.map(() => 'bytes32'), hashes);
  return keccak256(packed);
}

export const useGaslessPurchase = ({ eventTicketAddress, chainId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [txHash, setTxHash] = useState(null);

  const purchaseBatch = useCallback(
    async (items) => {
      setLoading(true);
      setError(null);
      setSuccess(false);
      setTxHash(null);

      try {
        if (!window.ethereum) throw new Error('Wallet not connected');
        if (!Array.isArray(items) || items.length === 0) throw new Error('No items provided');

        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const user = await signer.getAddress();
        const net = await provider.getNetwork();
        const resolvedChainId = Number(chainId ?? net.chainId);

        const contractAddr = eventTicketAddress || process.env.REACT_APP_EVENT_TICKET_ADDRESS;
        if (!contractAddr) throw new Error('Event ticket contract address not configured');

        // Sort items by ticketTypeId
        const sortedItems = [...items].sort((a, b) => a.ticketTypeId - b.ticketTypeId);

        // Compute itemsHash (must match contract's _hashMintItems)
        const itemsHash = computeItemsHash(sortedItems);

        // Generate nonce (contract uses bitmap, not sequential)
        const nonce = String(Date.now() * 1000 + Math.floor(Math.random() * 1000));

        // Build domain
        const domain = getDomain(resolvedChainId, contractAddr);

        // Sign EIP-712
        const value = { user, itemsHash, nonce };
        console.log('[useGaslessPurchase] Signing EIP-712:', { domain, value, items: sortedItems });
        const signature = await signer.signTypedData(domain, TYPES_BATCH, value);

        // Try server relayer first (gasless — server pays gas)
        const relayerUrl = getRelayerMintUrl();
        if (relayerUrl) {
          console.log('[useGaslessPurchase] Sending to relayer:', relayerUrl);
          try {
            const relayerHeaders = { 'Content-Type': 'application/json' };
            if (RELAYER_API_KEY) relayerHeaders['X-API-Key'] = RELAYER_API_KEY;
            const response = await fetch(relayerUrl, {
              method: 'POST',
              headers: relayerHeaders,
              body: JSON.stringify({
                user,
                items: sortedItems.map(i => ({
                  ticketTypeId: Number(i.ticketTypeId),
                  amount: Number(i.amount),
                })),
                itemsHash,
                nonce,
                signature,
                contractAddress: contractAddr,
              }),
            });

            const result = await response.json();
            if (response.ok && result.ok) {
              console.log('[useGaslessPurchase] Relayer TX confirmed:', result.txHash);
              setSuccess(true);
              setTxHash(result.txHash);
              return { ok: true, txHash: result.txHash };
            }
            // Relayer returned error — fall through to direct call
            console.warn('[useGaslessPurchase] Relayer error, falling back to direct:', result.error);
          } catch (relayerErr) {
            console.warn('[useGaslessPurchase] Relayer unreachable, falling back to direct:', relayerErr.message);
          }
        }

        // FALLBACK: Call contract directly (user pays gas)
        const eventTicket = new Contract(contractAddr, EventTicketABI.abi || EventTicketABI, signer);
        const mintItems = sortedItems.map(item => ({
          ticketTypeId: _BigInt(item.ticketTypeId),
          amount: _BigInt(item.amount),
        }));

        console.log('[useGaslessPurchase] Calling executeMintTicketBatch on-chain (direct)...');
        const tx = await eventTicket.executeMintTicketBatch(user, mintItems, nonce, signature);
        console.log('[useGaslessPurchase] TX sent:', tx.hash);

        const receipt = await tx.wait();
        console.log('[useGaslessPurchase] TX confirmed:', receipt.hash);

        setSuccess(true);
        setTxHash(receipt.hash);
        return { ok: true, txHash: receipt.hash };
      } catch (e) {
        const msg = e?.reason || e?.shortMessage || e?.message || 'Purchase failed';
        console.error('[useGaslessPurchase] Error:', msg, e);
        setError(msg);
        return { ok: false, error: msg };
      } finally {
        setLoading(false);
      }
    },
    [eventTicketAddress, chainId]
  );

  return { purchaseBatch, loading, error, success, txHash };
};
